import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { getHomepageConfig, getProductById } from '../services/homepage.service';
import ProductCard from '../components/ui/ProductCard';
import Section from '../components/ui/Section';
import { motion } from 'framer-motion';
import { fadeUp, staggerContainer } from '../lib/animations';

const DealsPage = () => {
    const { t } = useTranslation();
    const [products, setProducts] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const loadDeals = async () => {
            try {
                // 1. Get Config
                const config = await getHomepageConfig();

                // Check for dealsPage configuration
                if (!config || !config.dealsPage || !config.dealsPage.length) {
                    setLoading(false);
                    return;
                }

                const dealItems = config.dealsPage.filter(item => {
                    // Handle both simple ID strings and objects
                    if (typeof item === 'string') return true;
                    return item.isActive !== false;
                });

                if (dealItems.length === 0) {
                    setLoading(false);
                    return;
                }

                // Extract IDs
                const ids = dealItems.map(item => typeof item === 'string' ? item : item.id);

                // 2. Fetch Products
                const promises = ids.map(id => getProductById(id));
                const results = await Promise.all(promises);
                const fetchedDocs = results.filter(Boolean);

                // 3. Sort by Config Order
                const ordered = dealItems.map(item => {
                    const id = typeof item === 'string' ? item : item.id;
                    const p = fetchedDocs.find(fd => fd.id === id);
                    if (!p) return null;
                    return p;
                }).filter(Boolean);

                setProducts(ordered);

            } catch (err) {
                console.error("Failed to load deals products", err);
            } finally {
                setLoading(false);
            }
        };

        loadDeals();
    }, []);

    return (
        <div className="bg-fecos-bg min-h-screen pb-24 pt-10">
            <div className="max-w-[1400px] mx-auto px-6 lg:px-12">

                {/* HEADER */}
                <div className="flex items-end justify-between mb-12">
                    <div>
                        <h1 className="text-4xl lg:text-5xl font-bold text-fecos-text tracking-tight mb-4">
                            {t("dealsPage.title", "Günün Fırsatları")}
                        </h1>
                        <p className="text-fecos-gray">{t("dealsPage.subtitle", "Seçilmiş fırsat ürünleri")}</p>
                    </div>
                </div>

                {/* CONTENT */}
                {loading ? (
                    <div className="flex justify-center py-20 text-fecos-gray">{t("common.loading", "Yükleniyor...")}</div>
                ) : products.length === 0 ? (
                    <div className="flex flex-col items-center justify-center py-20 text-fecos-gray bg-fecos-surface rounded-3xl p-10 shadow-sm">
                        <p className="text-lg">{t("dealsPage.empty", "Henüz fırsat ürünü eklenmedi.")}</p>
                    </div>
                ) : (
                    <motion.div
                        variants={staggerContainer}
                        initial="hidden"
                        animate="visible"
                        className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8"
                    >
                        {products.map((p) => (
                            <motion.div variants={fadeUp} key={p.id}>
                                <Section>
                                    <ProductCard product={p} />
                                </Section>
                            </motion.div>
                        ))}
                    </motion.div>
                )}
            </div>
        </div>
    );
};

export default DealsPage;
